import { QueryClient } from "@tanstack/react-query";

const FIVE_MIN = 5 * 60 * 1000;
const THIRTY_MIN = 30 * 60 * 1000;

interface AxiosLikeError {
    response?: { status?: number };
}

/**
 * 4xx client errors (auth 401, not-found 404, validation 422, …) are permanent
 * for a given request: retrying them only stalls the UI — e.g. a row with no
 * content score returns 404 on `/row/:id/score`, and retrying it 3× with backoff
 * stalls the detail page for ~9s. Only transient errors (5xx, network) are retried.
 */
export const isClientError = (error: unknown): boolean => {
    const status = (error as AxiosLikeError)?.response?.status;
    return status !== undefined && status >= 400 && status < 500;
};

/**
 * Shared react-query client for the app.
 *
 * Defaults: 5-min stale time, 30-min cache retention, retry up to 3× on
 * transient errors only (see {@link isClientError}), and no retry on mutations.
 */
export const queryClient: QueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: FIVE_MIN,
            gcTime: THIRTY_MIN,
            retry: (failureCount: number, error: unknown) =>
                !isClientError(error) && failureCount < 3,
            refetchOnWindowFocus: true,
            refetchOnReconnect: true,
        },
        mutations: {
            retry: false,
        },
    },
});
